import { supabase } from "@/integrations/supabase/client";
import { Candidate, CandidateCustomFieldsValues } from "@/types/candidate";
import { toast } from "@/components/ui/use-toast";

export const updateCandidate = async (
    id: string,
    updatedData: Partial<Candidate>,
    customFields: CandidateCustomFieldsValues = {}
): Promise<boolean> => {
    if (!id) return false;

    try {
        // First get the current candidate data to check if we need to trigger AI analysis
        const { data: currentCandidate, error: fetchError } = await supabase
            .from('candidates')
            .select('*')
            .eq('id', id)
            .single();

        if (fetchError) {
            console.error("Error fetching current candidate data:", fetchError);
            toast({
                title: "Error",
                description: "Could not fetch current candidate data",
                variant: "destructive"
            });
            return false;
        }

        // Make sure we're not sending any fields that don't exist in the database
        const dataToUpdate: Record<string, any> = {};

        // Only include fields that exist in the database table
        if (updatedData.name !== undefined) dataToUpdate.name = updatedData.name;
        if (updatedData.email !== undefined) dataToUpdate.email = updatedData.email;
        if (updatedData.phone !== undefined) dataToUpdate.phone = updatedData.phone;
        if (updatedData.function !== undefined) dataToUpdate.function = updatedData.function;
        if (updatedData.skills !== undefined) {
            // Ensure skills is always an array
            if (typeof updatedData.skills === 'string') {
                dataToUpdate.skills = (updatedData.skills as string).split(',').map(s => s.trim()).filter(Boolean);
            } else if (Array.isArray(updatedData.skills)) {
                dataToUpdate.skills = updatedData.skills;
            } else {
                dataToUpdate.skills = [];
            }
        }
        if (updatedData.manager !== undefined) dataToUpdate.manager = updatedData.manager;
        if (updatedData.status !== undefined) dataToUpdate.status = updatedData.status;
        if (updatedData.unavailable_until !== undefined) dataToUpdate.unavailable_until = updatedData.unavailable_until;
        if (updatedData.linkedin !== undefined) dataToUpdate.linkedin = updatedData.linkedin;
        if (updatedData.linkedin_json !== undefined) dataToUpdate.linkedin_json = updatedData.linkedin_json;
        if (updatedData.location !== undefined) dataToUpdate.location = updatedData.location;
        if (customFields && Object.keys(customFields).length > 0) dataToUpdate.custom_fields = customFields;

        const { error } = await supabase
            .from('candidates')
            .update(dataToUpdate)
            .eq('id', id);

        if (error) {
            console.error("Error updating candidate:", error);
            toast({
                title: "Error",
                description: `Failed to update candidate: ${error.message}`,
                variant: "destructive"
            });
            throw error;
        }

        // NOTE: History tracking is now handled automatically by database triggers
        // No manual history logging needed here

        // Only trigger AI analysis if function, location, or skills have actually changed
        function normalizeString(val: any) {
            return (val === null || val === undefined) ? '' : String(val).trim();
        }
        function normalizeSkills(val: any) {
            if (!val) return [];
            if (Array.isArray(val)) return val.map(s => normalizeString(s)).sort();
            if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean).sort();
            return [];
        }

        const functionChanged = normalizeString(currentCandidate.function) !== normalizeString(updatedData.function);
        //const locationChanged = normalizeString(currentCandidate.location) !== normalizeString(updatedData.location);
        const skillsChanged = JSON.stringify(normalizeSkills(currentCandidate.skills)) !== JSON.stringify(normalizeSkills(updatedData.skills));

        if (functionChanged || skillsChanged) 
            try {
                await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
                    method: 'POST',
                    body: {
                        entityId: id,
                        entityType: "candidate",
                        analysisType: "analyse-resume",
                        actionOnComplete: "update-candidate",
                    }
                });
                // AI analysis triggered for updated candidate
            } catch (analysisError) {
                console.error("Error triggering AI analysis:", analysisError);
                // Don't fail the update if AI analysis fails
            }

        toast({
            title: "Success",
            description: "Candidate updated successfully"
        });
        return true;
    } catch (error) {
        console.error("Error updating candidate:", error);
        return false;
    }
};
